
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { services } from '../data';
import { Service } from '../types';

const fadeInUp = {
  hidden: { opacity: 0, y: 50, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const Services = () => {
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const { scrollY } = useScroll();
  const yHero = useTransform(scrollY, [0, 800], [0, 300]);

  // Close modal when clicking escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedService(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <main className="font-geist text-primary bg-white min-h-screen">
      <style>{`
        .glass-card {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(241, 245, 249, 1);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
        }
        .hero-title-mask {
          background: linear-gradient(to bottom, #ffffff 30%, #cbd5e1 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center overflow-hidden bg-primary">
        <motion.div
          className="absolute inset-0 z-0"
          style={{ y: yHero }}
        >
          <img
            src="https://aadsjolsgasycbxbbjzx.supabase.co/storage/v1/object/public/Medical%20bucket/pexels-cottonbro-7579823.jpg"
            alt="Medical Professional"
            className="w-full h-full object-cover brightness-[0.85] scale-105"
            loading="eager"
            fetchPriority="high"
          />
          {/* Lighter, directional gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/50 to-primary/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent"></div>
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full">
          <motion.div
            className="max-w-4xl pt-24 md:pt-32"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
            }}
          >
            <motion.div variants={fadeInUp} className="hidden md:flex items-center gap-4 mb-8">
              <span className="h-0.5 w-16 bg-accent"></span>
              <span className="text-accent text-xs font-bold uppercase tracking-[0.5em]">Medical Practice</span>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl lg:text-7xl font-sans-jakarta font-extrabold hero-title-mask mb-10 leading-[1.1] tracking-tight drop-shadow-lg">
              Our <span className="text-teal-400 italic">Medical</span> Services
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-slate-100 text-base md:text-lg font-light max-w-xl leading-relaxed border-l border-white/30 pl-8 drop-shadow-md">
              Providing a comprehensive range of medical expertise with a commitment to unhurried, patient-centered care.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="relative">
        <div className="sticky top-0 h-screen w-full overflow-hidden z-0">
          <img
            src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/pexels-tara-winstead-7723532.jpg"
            className="w-full h-full object-cover"
            alt="Practice Background"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-white/10"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white/90"></div>
        </div>

        <div className="relative z-10 -mt-[100vh] py-32 px-6 md:px-8">
          <div className="max-w-7xl mx-auto">

            <motion.div
              className="mb-24 flex flex-col md:flex-row md:items-end justify-between gap-12"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
            >
              <div className="max-w-3xl">
                <span className="text-accent font-bold text-xs uppercase tracking-[0.6em] mb-4 block">Our Capabilities</span>
                <h2 className="text-5xl md:text-7xl font-sans-jakarta font-extrabold text-primary leading-[1.1] tracking-tight">Expert <span className="text-teal-500 italic">Clinical</span> Specialties</h2>
              </div>
              <div className="text-right hidden md:block">
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-2">Patient Focused</p>
                <div className="h-0.5 w-12 bg-accent ml-auto"></div>
              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              {services.map((service, index) => {
                const staggerClass = index % 3 === 1 ? 'lg:mt-16' : index % 3 === 2 ? 'lg:mt-32' : '';
                const tabletStagger = index % 2 === 1 ? 'md:mt-12' : '';

                return (
                  <motion.div
                    key={service.id}
                    variants={fadeInUp}
                    className={`group relative p-12 flex flex-col h-fit min-h-[400px] rounded-[3rem] transition-all duration-700 cursor-pointer transform-gpu ${staggerClass} ${tabletStagger}`}
                    onClick={() => setSelectedService(service)}
                    whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  >
                    <div className="glass-card absolute inset-0 rounded-[3rem] group-hover:shadow-[0_40px_80px_-20px_rgba(15,23,42,0.1)] group-hover:border-accent/30 transition-all duration-500"></div>

                    <div className="relative z-10 flex flex-col h-full pointer-events-none">
                      <div className="w-16 h-16 mb-10 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-accent shadow-sm group-hover:bg-accent group-hover:text-white group-hover:scale-110 transition-all duration-700">
                        <span className="material-symbols-outlined text-4xl font-light">{service.icon}</span>
                      </div>

                      <h3 className="text-2xl font-geist font-bold text-primary mb-6 leading-tight tracking-tight">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 mb-10 leading-relaxed text-base font-light opacity-90 group-hover:opacity-100 transition-opacity">
                        {service.description}
                      </p>

                      {/* Footer Actions */}
                      <div className="mt-auto pt-8 border-t border-slate-900/10 flex items-center justify-between gap-4 pointer-events-auto">
                        {/* Read More Button (Opens Modal) */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedService(service);
                          }}
                          className="flex items-center gap-3 text-[10px] font-bold tracking-[0.3em] text-primary/70 hover:text-accent transition-colors uppercase group/btn"
                        >
                          {service.actionText}
                          <span className="material-symbols-outlined text-lg transition-transform group-hover/btn:translate-x-1">arrow_forward</span>
                        </button>

                        {/* Book Button (Direct Link) */}
                        <a
                          href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:bg-accent hover:border-accent hover:text-white transition-all duration-300 shadow-sm"
                          title="Book Appointment"
                        >
                          <span className="material-symbols-outlined text-lg">calendar_month</span>
                        </a>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section id="contact" className="py-20 px-6 md:px-8 bg-white relative scroll-mt-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="relative overflow-hidden bg-primary rounded-[2.5rem] md:rounded-[3.5rem] p-10 md:p-16 lg:py-20 text-white shadow-2xl text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-[-30%] left-[-15%] w-[80%] aspect-square bg-accent rounded-full blur-[2px] opacity-100"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-primary/40 to-transparent"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center max-w-4xl mx-auto">
              <span className="text-[10px] font-bold uppercase tracking-[0.4em] mb-6 opacity-80">Book Now</span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-sans-jakarta font-extrabold mb-10 leading-[1.1] tracking-tight">
                Book your medical appointment today
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a
                  href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 bg-white px-10 py-5 rounded-full text-primary font-bold text-xs tracking-[0.15em] uppercase hover:bg-teal-500 hover:text-white transition-all shadow-xl transform hover:-translate-y-1"
                >
                  <span className="material-symbols-outlined text-xl group-hover:scale-110 transition-transform">calendar_month</span>
                  Book an appointment
                </a>
                <Link to="/team" className="group flex items-center gap-4 border-2 border-white px-10 py-5 rounded-full text-white font-bold text-xs tracking-[0.15em] uppercase hover:bg-white hover:text-primary transition-all transform hover:-translate-y-1">
                  <span className="material-symbols-outlined text-xl font-light">groups</span>
                  Meet our Team
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Details Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={() => setSelectedService(null)}
            ></div>
            <motion.div
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="p-8 md:p-12 overflow-y-auto w-full">
                <div className="flex justify-between items-start mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center text-accent">
                    <span className="material-symbols-outlined text-4xl">{selectedService.icon}</span>
                  </div>
                  <button
                    onClick={() => setSelectedService(null)}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                <h2 className="text-3xl md:text-4xl font-sans-jakarta font-bold text-primary mb-6">
                  {selectedService.title}
                </h2>

                <div className="prose prose-slate max-w-none mb-10">
                  <p className="text-lg text-slate-600 leading-relaxed font-light">
                    {selectedService.longDescription || selectedService.description}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-slate-100">
                  <a
                    href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-accent text-white px-8 py-4 rounded-full text-center text-xs font-bold uppercase tracking-widest hover:bg-teal-700 transition-colors shadow-lg shadow-accent/20 flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-lg">calendar_month</span>
                    Book Appointment
                  </a>
                  <button
                    onClick={() => setSelectedService(null)}
                    className="px-8 py-4 rounded-full text-center text-xs font-bold uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-colors border border-slate-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </main>
  );
};

export default Services;
